import { useState } from "react";
import { Box, Button } from "@material-ui/core";
import { LocationGroupsManagerDialog } from "./LocationGroupsManagerDialog";
import type { ReactNode } from "react";

/**
 * The call-to-action a feature update uses to hand the realtor straight to their
 * location groups.
 *
 * ── Why a component and not a link ───────────────────────────────────────────
 *
 * The groups manager is a DIALOG, opened from a cog inside the area picker's
 * dropdown. The update's button used to point at `/dashboard/settings` and leave
 * the realtor to find the card themselves — a worse ending than the sentence
 * promised.
 *
 * A route DOES exist now (`/dashboard/settings/areas`), but this still opens the
 * dialog: the update's sentence is "create your first group", and the dialog is
 * where that happens in one click. The route is the place to go and MANAGE them.
 *
 * Feature-update content is raw HTML rendered through `Markdown` with
 * `rehypeRaw`, so it cannot call anything. `FeatureUpdatesModal` already solves
 * that for the before/after slider by mapping a custom element to a React
 * component (`markdownComponents`), and this is the same mechanism:
 *
 *     <opengroups>🗂️ ליצירת הקבוצה הראשונה</opengroups>
 *
 * The label stays in the update's own content, where the rest of its copy lives.
 *
 * ── The updates modal deliberately stays open behind it ──────────────────────
 *
 * Closing it would unmount this button and take the dialog with it. Stacking is
 * also the better ending: creating a group takes a minute, and closing it returns
 * the realtor to the update they were reading rather than to nothing.
 */

type LocationGroupsCtaButtonProps = {
  children?: ReactNode;
};

export function LocationGroupsCtaButton({
  children
}: LocationGroupsCtaButtonProps): JSX.Element {
  const [open, setOpen] = useState(false);

  return (
    <>
      {/* A SPAN with `display: block`, not a div.
          Markdown wraps this element in a paragraph — verified against the real
          remark/rehype pipeline — and a div inside a `<p>` is invalid HTML, so the
          browser auto-closes the paragraph and the centering goes with it. A span
          is phrasing content, so it nests legally, and `display: block` still
          gives it the full-width line it needs. (`<button>` is phrasing content
          too, so the button itself was never the problem.) */}
      <Box component="span" sx={{ display: "block", textAlign: "center", my: 3 }}>
        <Button
          onClick={() => setOpen(true)}
          sx={{
            // Matches the lime CTA the other feature updates render as a plain
            // anchor, so one update does not look like a different product.
            px: 3,
            py: 1.25,
            fontSize: 15,
            fontWeight: "bold",
            borderRadius: "8px",
            color: "#212121",
            backgroundColor: "#CDDC39",
            "&:hover": { backgroundColor: "#C0CA33" }
          }}
        >
          {children}
        </Button>
      </Box>
      <LocationGroupsManagerDialog open={open} onClose={() => setOpen(false)} />
    </>
  );
}
